import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import axiosInstance from '../../utils/setupAxios';
import {
  ListDesktop,
  ListMobile,
  MobileCard,
  MobileCardTop,
  MobileCardTitle,
  MobileCardRow,
  MobileCardFooter,
} from '../common/ListMobileLayout';

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 500;

const formatDate = (ts) => {
  if (!ts) return '-';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? String(ts) : d.toLocaleDateString();
};

const formatPrice = (v) => {
  if (v == null || v === '') return null;
  const n = parseFloat(v);
  if (isNaN(n) || n === 0) return null;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const isValidImgUrl = (url) => typeof url === 'string' && url.trim() !== '' && !/\.html?$/i.test(url);

const tcgplayerUrl = (id) => (id ? `https://www.tcgplayer.com/product/${id}` : null);

const emptyFilters = {
  games: [],
  series: [],
  character_classes: [],
  rarities: [],
};

export default function CardsList() {
  const [searchInput, setSearchInput] = useState('');
  const [gameInput, setGameInput] = useState('');
  const [seriesInput, setSeriesInput] = useState('');
  const [classInput, setClassInput] = useState('');
  const [rarityInput, setRarityInput] = useState('');
  const [hasTcgInput, setHasTcgInput] = useState('');

  const [query, setQuery] = useState('');
  const [gameFilter, setGameFilter] = useState('');
  const [seriesFilter, setSeriesFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [rarityFilter, setRarityFilter] = useState('');
  const [hasTcgFilter, setHasTcgFilter] = useState('');

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterOptions, setFilterOptions] = useState(emptyFilters);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);

  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchCards = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, items_per_page: PAGE_SIZE };
      if (query) params.q = query;
      if (gameFilter) params.game_uid = gameFilter;
      if (seriesFilter) params.series_uid = seriesFilter;
      if (classFilter) params.character_class = classFilter;
      if (rarityFilter) params.rarity_full_name = rarityFilter;
      if (hasTcgFilter !== '') params.has_tcgplayer = hasTcgFilter;

      const response = await axiosInstance.get('/holo/admin/cards', { params });
      if (response.data?.success) {
        const data = response.data.data || {};
        setItems(Array.isArray(data.items) ? data.items : []);
        setTotal(Number(data.objects?.total ?? 0));
      } else {
        setError(response.data?.message || 'Failed to load cards');
        setItems([]);
        setTotal(0);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load cards');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, query, gameFilter, seriesFilter, classFilter, rarityFilter, hasTcgFilter]);

  useEffect(() => {
    let cancelled = false;
    axiosInstance.get('/holo/admin/cards/filters')
      .then((response) => {
        if (cancelled) return;
        const d = response.data?.data || {};
        setFilterOptions({
          games:             Array.isArray(d.games) ? d.games : [],
          series:            Array.isArray(d.series) ? d.series : [],
          character_classes: Array.isArray(d.character_classes) ? d.character_classes : [],
          rarities:          Array.isArray(d.rarities) ? d.rarities : [],
        });
      })
      .catch(() => { if (!cancelled) setFilterOptions(emptyFilters); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setQuery(searchInput.trim());
      setGameFilter(gameInput);
      setSeriesFilter(seriesInput);
      setClassFilter(classInput);
      setRarityFilter(rarityInput);
      setHasTcgFilter(hasTcgInput);
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput, gameInput, seriesInput, classInput, rarityInput, hasTcgInput]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const handleClear = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput('');
    setGameInput('');
    setSeriesInput('');
    setClassInput('');
    setRarityInput('');
    setHasTcgInput('');
    setQuery('');
    setGameFilter('');
    setSeriesFilter('');
    setClassFilter('');
    setRarityFilter('');
    setHasTcgFilter('');
    setPage(1);
  };

  const hasFilters = searchInput || gameInput || seriesInput || classInput || rarityInput || hasTcgInput;

  const pageNumbers = () => {
    const start = Math.max(1, Math.min(page - 2, lastPage - 4));
    const end = Math.min(lastPage, start + 4);
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const PaginationBar = () => (
    <div className="d-flex align-items-center px-3 py-2">
      <p className="m-0 text-muted small">
        Page <strong>{page}</strong> / <strong>{lastPage}</strong>
        {' · '}{(total ?? 0).toLocaleString()} total
      </p>
      {lastPage > 1 && (
        <ul className="pagination pagination-sm m-0 ms-auto">
          <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
          </li>
          {pageNumbers().map((n) => (
            <li key={n} className={`page-item ${n === page ? 'active' : ''}`}>
              <button className="page-link" onClick={() => setPage(n)}>{n}</button>
            </li>
          ))}
          <li className={`page-item ${page >= lastPage ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setPage((p) => Math.min(lastPage, p + 1))}>›</button>
          </li>
        </ul>
      )}
    </div>
  );

  const resolveGameName = (uid) => filterOptions.games.find((g) => g.uid === uid)?.name || uid || '-';
  const resolveSeriesName = (uid) => filterOptions.series.find((s) => s.uid === uid)?.name || uid || '-';

  const renderCardMobileCard = (card) => {
    const thumb = isValidImgUrl(card.vendor_image_url) ? card.vendor_image_url : null;
    return (
      <MobileCard key={card.card_uid}>
        <MobileCardTop>
          {thumb ? (
            <img
              src={thumb}
              alt={card.name}
              style={{ width: 48, height: 67, objectFit: 'cover', borderRadius: 6, border: '1px solid #dee2e6', flexShrink: 0 }}
            />
          ) : null}
          <MobileCardTitle>{card.name || '-'}</MobileCardTitle>
        </MobileCardTop>
        <MobileCardRow label="UID" mono>{card.card_uid}</MobileCardRow>
        <MobileCardRow label="Game">{resolveGameName(card.game_uid)}</MobileCardRow>
        <MobileCardRow label="Series">{resolveSeriesName(card.series_uid)}</MobileCardRow>
        <MobileCardRow label="Number">{card.number_nr || '-'}</MobileCardRow>
        <MobileCardRow label="Rarity">{card.rarity_full_name || '-'}</MobileCardRow>
        <MobileCardRow label="Type">{card.character_class || '-'}</MobileCardRow>
        <MobileCardRow label="Artist">{card.artist || '-'}</MobileCardRow>
        <MobileCardRow label="Added">{formatDate(card.created_at)}</MobileCardRow>
        <MobileCardFooter>
          <button className="btn btn-sm btn-outline-primary" onClick={() => setSelected(card)}>
            View
          </button>
        </MobileCardFooter>
      </MobileCard>
    );
  };

  return (
    <div className="dashboard-list-page">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h2 className="page-title">Card List</h2>
            {!loading && (
              <div className="text-muted mt-1">
                {(total ?? 0).toLocaleString()} cards in database
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-sm-4">
              <label className="form-label mb-1">Name / Card UID / Number</label>
              <input
                className="form-control"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Flareon, c7om92cv, 202/187…"
              />
            </div>
            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Game</label>
              <select className="form-select" value={gameInput} onChange={(e) => setGameInput(e.target.value)}>
                <option value="">All games</option>
                {filterOptions.games.map((g) => (
                  <option key={g.uid} value={g.uid}>{g.name || g.uid}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Series</label>
              <select className="form-select" value={seriesInput} onChange={(e) => setSeriesInput(e.target.value)}>
                <option value="">All series</option>
                {filterOptions.series.map((s) => (
                  <option key={s.uid} value={s.uid}>{s.name || s.uid}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Card Type</label>
              <select className="form-select" value={classInput} onChange={(e) => setClassInput(e.target.value)}>
                <option value="">All types</option>
                {filterOptions.character_classes.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Rarity</label>
              <select className="form-select" value={rarityInput} onChange={(e) => setRarityInput(e.target.value)}>
                <option value="">All rarities</option>
                {filterOptions.rarities.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">TCGplayer</label>
              <select className="form-select" value={hasTcgInput} onChange={(e) => setHasTcgInput(e.target.value)}>
                <option value="">All</option>
                <option value="1">Has TCGplayer</option>
                <option value="0">No TCGplayer</option>
              </select>
            </div>
            {hasFilters && (
              <div className="col-auto">
                <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>
                  Clear filter
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {error && <div className="alert alert-danger m-3 mb-0">{error}</div>}

        {!loading && total > 0 && <PaginationBar />}

        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-muted py-5">No cards found.</div>
        ) : (
          <>
            <ListDesktop>
              <table className="table table-vcenter table-hover card-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Image</th>
                    <th style={{ width: 110 }}>Card UID</th>
                    <th>Name</th>
                    <th style={{ width: 140 }}>Game</th>
                    <th style={{ width: 160 }}>Series</th>
                    <th style={{ width: 90 }}>Number</th>
                    <th style={{ width: 150 }}>Rarity</th>
                    <th style={{ width: 100 }}>Type</th>
                    <th style={{ width: 140 }}>Artist</th>
                    <th style={{ width: 80 }}>TCG</th>
                    <th style={{ width: 110 }}>Added</th>
                    <th style={{ width: 70 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((card) => {
                    const thumb = isValidImgUrl(card.vendor_image_url) ? card.vendor_image_url : null;
                    const tcgUrl = tcgplayerUrl(card.tcgplayer_id);
                    return (
                      <tr key={card.card_uid}>
                        <td>
                          {thumb ? (
                            <img
                              src={thumb}
                              alt={card.name}
                              style={{ width: 42, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #dee2e6' }}
                            />
                          ) : (
                            <div style={{ width: 42, height: 60, background: '#f1f3f4', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#adb5bd', fontSize: 18 }}>
                              ○
                            </div>
                          )}
                        </td>
                        <td><code className="small">{card.card_uid}</code></td>
                        <td>
                          <div className="fw-semibold">{card.name || '-'}</div>
                        </td>
                        <td>
                          <span className="badge bg-blue-lt text-blue" style={{ fontWeight: 500 }}>
                            {resolveGameName(card.game_uid)}
                          </span>
                        </td>
                        <td className="small">{resolveSeriesName(card.series_uid)}</td>
                        <td className="text-muted small">{card.number_nr || '-'}</td>
                        <td>
                          {card.rarity_full_name ? (
                            <span className="badge bg-orange-lt text-orange" style={{ fontSize: '0.7rem' }}>
                              {card.rarity_full_name}
                            </span>
                          ) : <span className="text-muted">-</span>}
                        </td>
                        <td>
                          {card.character_class ? (
                            <span className="badge bg-secondary-lt text-secondary" style={{ fontSize: '0.7rem' }}>
                              {card.character_class}
                            </span>
                          ) : <span className="text-muted">-</span>}
                        </td>
                        <td className="text-muted small">{card.artist || '-'}</td>
                        <td>
                          {tcgUrl ? (
                            <a href={tcgUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-link p-0">
                              {card.tcgplayer_id}
                            </a>
                          ) : <span className="text-muted">-</span>}
                        </td>
                        <td className="text-muted small">{formatDate(card.created_at)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setSelected(card)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ListDesktop>

            <ListMobile>
              {items.map((card) => renderCardMobileCard(card))}
            </ListMobile>
          </>
        )}

        {!loading && total > 0 && (
          <div className="card-footer p-0">
            <PaginationBar />
          </div>
        )}
      </div>

      {selected && (
        <CardDetailModal cardUid={selected.card_uid} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function CardDetailModal({ cardUid, onClose }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => { document.body.classList.remove('modal-open'); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance.get(`/holo/admin/cards/${cardUid}`)
      .then((response) => {
        if (cancelled) return;
        if (response.data?.success) {
          setCard(response.data.data);
        } else {
          setError(response.data?.message || 'Failed to load card');
        }
      })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.message || e.message || 'Failed to load card'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cardUid]);

  const extras = card?.extras || {};
  const game = extras.game || {};
  const series = extras.series || {};
  const sku = extras.sku || {};
  const skuList = Array.isArray(extras.sku_list) ? extras.sku_list : [];
  const vendorImg = isValidImgUrl(card?.vendor_image_url) ? card.vendor_image_url : null;
  const tcgUrl = tcgplayerUrl(card?.tcgplayer_id);

  return ReactDOM.createPortal(
    <>
      <div className="modal-backdrop fade show" />
      <div
        className="modal fade show"
        style={{ display: 'block' }}
        tabIndex="-1"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable modal-fullscreen-md-down">
          <div className="modal-content">

            <div className="modal-header">
              <div>
                <h5 className="modal-title mb-1">{card?.name || 'Card Detail'}</h5>
                <div className="d-flex flex-wrap gap-1">
                  {game.name && <span className="badge bg-blue-lt text-blue">{game.name}</span>}
                  {series.name && <span className="badge bg-secondary-lt text-secondary">{series.name}</span>}
                  {card?.rarity_full_name && card.rarity_full_name_show !== false && (
                    <span className="badge bg-orange-lt text-orange">{card.rarity_full_name}</span>
                  )}
                  {card?.number_nr && <span className="badge bg-green-lt text-green">{card.number_nr}</span>}
                  {sku.language && <span className="badge bg-purple-lt text-purple">{sku.language}</span>}
                </div>
              </div>
              <button type="button" className="btn-close ms-3" onClick={onClose} />
            </div>

            <div className="modal-body">
              {loading ? (
                <div className="d-flex justify-content-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : error ? (
                <div className="alert alert-danger">{error}</div>
              ) : card ? (
                <>
                  {vendorImg && (
                    <div className="mb-4">
                      <div className="text-muted small fw-semibold text-uppercase mb-2">Vendor Reference Image</div>
                      <a href={vendorImg} target="_blank" rel="noreferrer" className="d-inline-block" style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #dee2e6' }}>
                        <img
                          src={vendorImg}
                          alt={card.name}
                          style={{ width: 180, height: 250, objectFit: 'contain', background: '#f8f9fa', display: 'block' }}
                        />
                      </a>
                    </div>
                  )}

                  <div className="mb-4">
                    <div className="text-muted small fw-semibold text-uppercase mb-2">Overview</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px' }}>
                      {[
                        { label: 'Card UID', value: card.card_uid },
                        { label: 'Card ID', value: card.card_id || '-' },
                        { label: 'Number', value: card.number_nr || '-' },
                        { label: 'Card Finish', value: sku.variant || '-' },
                        { label: 'Card Type', value: card.character_class || '-' },
                        { label: 'Artist', value: card.artist || '-' },
                        { label: 'Card Count', value: series.card_count ?? '-' },
                        { label: 'Market Price', value: formatPrice(sku.market_price) || '-' },
                        { label: 'Price Note', value: sku.price_comparison || '-' },
                        { label: 'TCGplayer', value: tcgUrl ? (
                          <a href={tcgUrl} target="_blank" rel="noreferrer">{card.tcgplayer_id}</a>
                        ) : '-' },
                        { label: 'Added', value: formatDate(card.created_at) },
                      ].map(({ label, value }) => (
                        <div key={label} style={{ minWidth: 140 }}>
                          <div className="text-muted small">{label}</div>
                          <div className="fw-semibold">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {card.description && (
                    <div className="mb-4">
                      <div className="text-muted small fw-semibold text-uppercase mb-2">Description</div>
                      <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
                        {card.description}
                      </div>
                    </div>
                  )}

                  {skuList.length > 0 && (
                    <div className="mb-4">
                      <div className="text-muted small fw-semibold text-uppercase mb-2">SKU List</div>
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Condition</th>
                              <th>Variant</th>
                              <th>Language</th>
                              <th>Market Price</th>
                              <th>Price Comparison</th>
                            </tr>
                          </thead>
                          <tbody>
                            {skuList.map((s, i) => {
                              const isDefault = sku.uid && s.uid === sku.uid;
                              return (
                                <tr key={s.uid || i} className={isDefault ? 'table-success' : undefined}>
                                  <td>{s.condition || '-'}</td>
                                  <td>{s.variant || '-'}</td>
                                  <td>{s.language || '-'}</td>
                                  <td>{formatPrice(s.market_price) || '-'}</td>
                                  <td className="small text-muted">{s.price_comparison || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
